import type { SiteCopy } from './types';

const en: SiteCopy = {
  meta: {
    title: 'UniteStream · UniteBase & UniteGo',
    description:
      'Two products for embodied AI teams: UniteBase unified data foundation and UniteGo minimal egocentric capture — from data infrastructure to field recording.',
    keywords:
      'UniteBase,UniteGo,embodied AI,data platform,data ingest,dataset catalog,egocentric capture,AI labeling,UniteStream',
  },
  nav: {
    products: 'Products',
    uniteBase: 'UniteBase',
    uniteGo: 'UniteGo',
    blog: 'Blog',
    contact: 'Contact',
    languageAria: 'Interface language',
  },
  blog: {
    eyebrow: 'Blog',
    listTitle: 'Latest posts',
    listSubtitle: 'Product updates, engineering notes, and embodied AI perspectives.',
    backToList: 'Back to blog',
  },
  hero: {
    eyebrow: 'Embodied AI',
    title: 'Embodied AI ·',
    titleAccent: 'Capture + Data Platform',
    subtitle:
      'UniteGo captures egocentric data in the field with one iPhone; UniteBase is the cloud platform for storage, catalog, and multimodal analytics in one place. From recording to model training — one toolchain for robotics teams.',
    ctaPrimary: 'Book a demo',
    ctaSecondary: 'Explore products',
  },
  products: {
    uniteGo: {
      title: 'UniteGo · Minimal capture',
      body: 'One iPhone, zero wearables. HD video and 3D hand tracks stay frame-locked in sync.',
      cta: 'Explore UniteGo →',
    },
    uniteBase: {
      title: 'UniteBase · Unified data platform',
      body: 'Cloud data platform in one place — S3 storage, global catalog, and multimodal analytics for robot teams.',
      cta: 'Explore UniteBase →',
    },
  },
  homeCta: {
    title: 'Ready to build your embodied data pipeline?',
    subtitle: 'Drop the heavy gear and scattered scripts — collaborate the modern way.',
    button: 'Get started with UniteStream',
  },
  contact: {
    eyebrow: 'Contact us',
    title: 'Get UniteBase / UniteGo deployment',
    subtitle: 'Leave your details and needs — we will reach out with the right deployment plan and support.',
    labels: {
      name: 'Name',
      email: 'Email',
      company: 'Company / Team',
      message: 'What you need',
    },
    placeholders: {
      name: 'How should we call you',
      email: 'name@company.com',
      company: 'Company or team name',
      message: 'Briefly describe your scenario, data scale, or deployment needs',
    },
    submit: 'Get deployment',
    sending: 'Sending…',
    success: 'Received! We will get back to you soon.',
    error: 'Submission failed. Please try again later.',
    note: 'By submitting you agree we use this info only to contact you.',
  },
  footer: {
    tagline: 'Embodied intelligence products',
    product: 'UniteStream',
    privacy: 'Privacy',
    cookies: 'Cookie settings',
    rights: 'All rights reserved.',
  },
  cookie: {
    title: 'Cookies & local storage',
    lead: 'We only use strictly necessary cookies and local storage for language preference and your acknowledgment record. This site does not use visit analytics or third-party tracking.',
    acknowledge: 'Got it',
    learnMore: 'Privacy notice',
    settingsTitle: 'Cookie settings',
    inventoryTitle: 'Storage used on this site',
    inventory: [
      {
        name: 'unitestream-cookie-consent',
        purpose: 'Records that you have read the cookie notice',
        duration: '12 months',
        type: 'Strictly necessary (localStorage)',
      },
      {
        name: 'URL parameter lang',
        purpose: 'Remembers your interface language (EN / 中文)',
        duration: 'Session / until you change it',
        type: 'Strictly necessary',
      },
    ],
  },
  privacy: {
    close: 'Got it',
    sections: [
      {
        title: 'Scope',
        body: 'This notice applies to the UniteStream marketing website (UniteBase and UniteGo product pages, blog, and contact form). Account and data handling inside product consoles is governed by those products’ own terms and policies.',
      },
      {
        title: 'Information we collect',
        body: 'When you submit the contact form, we may collect your name, email, company or team name, and message. We use strictly necessary local storage to remember language preference.',
      },
      {
        title: 'Use and legal bases',
        body: 'Contact details are used to respond and for pre-contract communication (GDPR: Art. 6(1)(b)(f)). We do not sell personal data. We do not share with unrelated third parties except as required by law or with your consent.',
      },
      {
        title: 'Cookies and local storage',
        body: 'This site uses only strictly necessary cookies and local storage (language preference and cookie notice acknowledgment). We do not use visit analytics, advertising, or third-party tracking.',
      },
      {
        title: 'Retention and your rights',
        body: 'Contact data is kept for as long as needed for business communication. You have rights of access, rectification, erasure, restriction, portability, and objection (GDPR Arts. 15–22), and may lodge a complaint with a supervisory authority. Contact us via the form to exercise rights.',
      },
      {
        title: 'International visitors and updates',
        body: 'The site is available globally. If you are in the EEA, UK, or other regions with similar laws, these rights apply to you. We may update this notice; material changes will be highlighted on the site.',
      },
    ],
  },
};

export default en;
